/* eslint-disable @typescript-eslint/no-explicit-any */
import { size, toString } from 'es-toolkit/compat';
import { FieldInputProps, FieldMetaProps, FormikContextType } from 'formik';
import { SelectInputProps as MUISelectInputProps } from '@material-ui/core/Select/SelectInput';
import { ReactNode } from 'react';

export function handleChangeFormField(
  field: FieldInputProps<any>,
  event: React.ChangeEvent<any>,
  onChange?: (evt: React.ChangeEvent<any>) => void
): void {
  field.onChange(event);

  if (onChange) {
    onChange(event);
  }
}

export function handleChangeFormSelectField(
  field: FieldInputProps<any>,
  event: React.ChangeEvent<{ name?: string; value: unknown }>,
  child: React.ReactNode,
  onChange?: MUISelectInputProps['onChange']
): void {
  field.onChange(event);

  if (onChange) {
    onChange(event, child);
  }
}

/**
 * this sets the value, mimicking what
 * it would do with the raw event
 *
 * @param field
 * @param value
 * @param onChange
 */
export function handleChangeFormFieldValue<T = any>(
  field: FieldInputProps<T>,
  form: FormikContextType<any>,
  value: T,
  onChange?: (value: T) => void
): void {
  form.setFieldValue(field.name, value, true);

  if (onChange) {
    onChange(value);
  }
}

export function handleBlurFormField(
  field: FieldInputProps<any>,
  event: React.FocusEvent<any>,
  onBlur?: (event: React.FocusEvent<any>) => void
) {
  field.onBlur(event);

  if (onBlur) {
    onBlur(event);
  }
}

/**
 * this sets the value, mimicking what
 * it would do with the raw event
 *
 * @param field
 * @param value
 * @param onBlur
 */
export function handleBlurFormFieldValue<T = any>(
  field: FieldInputProps<T>,
  form: FormikContextType<any>,
  value: T,
  onBlur?: (value: T) => void
): void {
  form.setFieldValue(field.name, value, true);
  form.setFieldTouched(field.name);

  if (onBlur) {
    onBlur(value);
  }
}

export function checkFormHasError(meta: FieldMetaProps<any>) {
  return !!(meta.error && meta.touched);
}

export function getFormHelperText(
  hasError: boolean,
  meta: FieldMetaProps<any>,
  helperText?: ReactNode
) {
  if (hasError) {
    return meta.error;
  }
  return helperText;
}

interface HelperTextOptions {
  group?: boolean;
  hasError?: boolean;
  helperText?: string | React.ReactNode;
}

interface HelperText {
  helperText?: string | React.ReactNode;
  errorText?: string;
  displayError?: boolean;
}

export function getFormSelectionControlsHelperTextState(
  meta: FieldMetaProps<any>,
  { hasError, group, helperText }: HelperTextOptions
): HelperText {
  return {
    helperText: group ? undefined : helperText,
    errorText: group ? undefined : meta.error,
    displayError: !group && hasError,
  };
}

export function getFormSelectionControlsGroupHelperTextState(
  meta: FieldMetaProps<any>,
  { hasError, helperText }: HelperTextOptions
): HelperText {
  return {
    helperText: helperText,
    errorText: meta.error,
    displayError: hasError,
  };
}

export function handleBlurFormattedTextField(
  event: React.FocusEvent<HTMLInputElement>,
  filter: InputFilter,
  setViewValue: (value: string) => void,
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
) {
  const modelValue = filter.parseViewValue(event.target.value);

  const newViewValue = filter.formatModelValue(modelValue);

  setViewValue(newViewValue as string);
  if (onBlur) {
    onBlur(event);
  }
}

export function handleChangeFormattedTextField(
  event: React.ChangeEvent<HTMLInputElement>,
  filter: InputFilter,
  setViewValue: (value: string) => void,
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
) {
  if (filter.formatOnChange) {
    const modelValue = filter.parseViewValue(event.target.value);
    const newViewValue = filter.formatModelValue(modelValue) + '';
    // formatOnChange did change, or may have changed, the view value. We
    // call onChange(event) below. So update some event.target.value accordingly.
    event.target.value = newViewValue;
    setViewValue(newViewValue);
  } else {
    setViewValue(event.target.value);
  }

  if (onChange) {
    onChange(event);
  }
}

export function handleFormattedTextFieldEffect(
  value: any,
  prevValue: any,
  filter: InputFilter,
  setViewValue: (value: string) => void,
  options?: { runOnChange?: boolean }
) {
  const runForChange = value !== prevValue && options?.runOnChange;
  // if the form gets reset, or goes to zero (or if runOnChange is set to true and the form changes )
  if (runForChange || (typeof value === 'string' && size(prevValue) > 0 && size(value) === 0)) {
    if (value) {
      const modelValue = filter.parseViewValue(value);
      const newViewValue = filter.formatModelValue(modelValue);
      setViewValue(newViewValue as string);
    } else {
      setViewValue(value || '');
    }
  }
}

export function handleChangeFormFormattedTextField(
  event: React.ChangeEvent<HTMLInputElement>,
  name: string,
  filter: InputFilter,
  setFieldValue: (name: string, value: string, shouldValidate: boolean) => void,
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
) {
  // we are skipping the formik event here because the wrong value will be sent to the validate function
  const modelValue = filter.parseViewValue(event.target.value);

  setFieldValue(name, toString(modelValue), true);

  if (onChange) {
    onChange(event);
  }
}

export function handleBlurFormFormattedTextField(
  event: React.FocusEvent<HTMLInputElement>,
  name: string,
  filter: InputFilter,
  setFieldValue: (name: string, value: string, shouldValidate: boolean) => void,
  setFieldTouched: (name: string) => void,
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
) {
  // we are skipping the formik event here because the wrong value will be sent to the validate function
  const modelValue = filter.parseViewValue(event.target.value);
  setFieldValue(name, toString(modelValue), true);
  setFieldTouched(name);

  if (onBlur) {
    onBlur(event);
  }
}
